import type { AdapterMappingGuidance } from "./types";

const pathSegmentPattern = /([^[.\]]+)|\[(\d+)\]/g;
const columnPathPattern = /^columns\[(\d+)\](\..+)$/;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const cloneValue = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map((item) => cloneValue(item)) as T;
  if (isRecord(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, cloneValue(item)])) as T;
  }
  return value;
};

const getPathSegments = (path: string): string[] =>
  Array.from(path.matchAll(pathSegmentPattern)).map((match) => match[1] ?? match[2]);

const setPathValue = (target: Record<string, unknown>, path: string, value: unknown): void => {
  const segments = getPathSegments(path);
  let current: Record<string, unknown> | unknown[] = target;

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    if (isLast) {
      if (Array.isArray(current)) current[Number(segment)] = cloneValue(value);
      else current[segment] = cloneValue(value);
      return;
    }

    const nextIsIndex = /^\d+$/.test(segments[index + 1]);
    const nextValue = Array.isArray(current) ? current[Number(segment)] : current[segment];
    if (!Array.isArray(nextValue) && !isRecord(nextValue)) {
      const replacement: Record<string, unknown> | unknown[] = nextIsIndex ? [] : {};
      if (Array.isArray(current)) current[Number(segment)] = replacement;
      else current[segment] = replacement;
      current = replacement;
    } else {
      current = nextValue;
    }
  });
};

const getColumnId = (value: unknown): string | undefined => {
  if (!isRecord(value) || typeof value.id !== "string") return undefined;
  return value.id;
};

export const remapAdapterMappingPath = (
  template: Record<string, unknown>,
  existing: Record<string, unknown>,
  path: string,
): string | undefined => {
  const match = path.match(columnPathPattern);
  if (!match) return path;

  const templateColumns = Array.isArray(template.columns) ? template.columns : [];
  const existingColumns = Array.isArray(existing.columns) ? existing.columns : [];
  const templateIndex = Number(match[1]);
  const templateId = getColumnId(templateColumns[templateIndex]);
  if (!templateId) return undefined;
  const existingIndex = existingColumns.findIndex((column) => getColumnId(column) === templateId);
  return existingIndex >= 0 ? `columns[${existingIndex}]${match[2]}` : undefined;
};

/** A new adapter-owned mapping: the template with the guidance's fixed (`equals`) values set. */
export const applyAdapterMappingRequirements = (
  templatePayload: Record<string, unknown>,
  guidance: AdapterMappingGuidance[] | undefined,
): Record<string, unknown> => {
  const payload = cloneValue(templatePayload);
  for (const requirement of (guidance ?? []).flatMap((item) => item.requirements)) {
    if (requirement.operator === "equals") setPathValue(payload, requirement.path, requirement.value);
  }
  return payload;
};
